import { BindingError } from "../BindingError.js";
import { ErrorAccessor } from "./ErrorAccessor.js";
import { QueryLanguage } from "../queryLanguage/QueryLanguage.js";
import { PlaceholderGenerator } from "../markers/PlaceholderGenerator.js";
import { getEntityMarker } from "../core/state/getEntityMarker.js";
class EntityAccessor {
  constructor(state, operations, runtimeId, key, name, fieldData, dataFromServer, hasUnpersistedChanges, errors, environment, getAccessor) {
    this.state = state;
    this.operations = operations;
    this.runtimeId = runtimeId;
    this.key = key;
    this.name = name;
    this.fieldData = fieldData;
    this.dataFromServer = dataFromServer;
    this.hasUnpersistedChanges = hasUnpersistedChanges;
    this.errors = errors;
    this.environment = environment;
    this.getAccessor = getAccessor;
  }
  get idOnServer() {
    return this.runtimeId.existsOnServer ? this.runtimeId.value : void 0;
  }
  get id() {
    return this.runtimeId.value;
  }
  get existsOnServer() {
    return this.runtimeId.existsOnServer;
  }
  addError(error) {
    return this.operations.addError(this.state, ErrorAccessor.normalizeError(error));
  }
  addEventListener(event, listener) {
    return this.operations.addEventListener(this.state, event, listener);
  }
  batchUpdates(performUpdates) {
    this.operations.batchUpdates(this.state, performUpdates);
  }
  connectEntityAtField(field, entityToConnect) {
    this.operations.connectEntityAtField(this.state, field, entityToConnect);
  }
  disconnectEntityAtField(field, initializeReplacement) {
    this.operations.disconnectEntityAtField(this.state, field, initializeReplacement);
  }
  deleteEntity() {
    this.operations.deleteEntity(this.state);
  }
  updateValues(fieldValuePairs) {
    this.batchUpdates((getAccessor) => {
      const entries = Array.isArray(fieldValuePairs) ? fieldValuePairs : Object.entries(fieldValuePairs);
      for (const [field, value] of entries) {
        getAccessor().getField(field).updateValue(value);
      }
    });
  }
  getField(field) {
    return this.getRelativeSingleField(QueryLanguage.desugarRelativeSingleField(field, this.environment));
  }
  getEntity(entity) {
    return this.getRelativeSingleEntity(QueryLanguage.desugarRelativeSingleEntity(entity, this.environment));
  }
  getEntityList(entityList) {
    return this.getRelativeEntityList(QueryLanguage.desugarRelativeEntityList(entityList, this.environment));
  }
  getRelativeSingleField(field) {
    return this.getRelativeSingleEntity(field).getAccessorByPlaceholder(
      PlaceholderGenerator.getFieldPlaceholder(field.field)
    );
  }
  getRelativeSingleEntity(relativeSingleEntity) {
    let relativeTo = this;
    for (const hasOneRelation of relativeSingleEntity.hasOneRelationPath) {
      relativeTo = relativeTo.getAccessorByPlaceholder(
        PlaceholderGenerator.getHasOneRelationPlaceholder(hasOneRelation)
      );
    }
    return relativeTo;
  }
  getRelativeEntityList(entityList) {
    return this.getRelativeSingleEntity(entityList).getAccessorByPlaceholder(
      PlaceholderGenerator.getHasManyRelationPlaceholder(entityList.hasManyRelation)
    );
  }
  getParent() {
    const blueprint = this.state.blueprint;
    if (blueprint.type === "subTree") {
      return void 0;
    }
    return blueprint.parent.getAccessor();
  }
  getMarker() {
    return getEntityMarker(this.state);
  }
  getAccessorByPlaceholder(placeholderName) {
    const record = this.fieldData.get(placeholderName);
    if (record === void 0) {
      throw new BindingError(
        `EntityAccessor: unknown field placeholder '${placeholderName} on ${this.name}'. Unless this is just a typo, this is typically caused by one of the following:
	\u2022 Trying to access a field that has not been registered during static render, and thus lacks a marker and an accessor.
	\u2022 Misusing an EntityAccessor getter. If you used one of the getRelative[\u2026] family, please make sure all parameters match the marker tree exactly.

For more information, please consult the documentation.

`
      );
    }
    return record.getAccessor();
  }
}
export {
  EntityAccessor
};
//# sourceMappingURL=EntityAccessor.js.map
